/**
 * B 端模块注册表（ADR-0004 SSOT）
 * 登录后按 entitlement 拼侧栏：通用区 + 各模块区块
 */
import type { ConsoleMenuItem } from '@/views/merchant/registry/consoles'
import {
  CAPACITY,
  HOTEL,
  LIVE_MENU,
  MALL,
  O2O,
  RENTAL,
  SCENIC,
  XUANXUE
} from '@/views/merchant/registry/consoles-export'
import { STORE_BASE, storePath } from '@/views/merchant/registry/paths'

/** 直播管理：挂在商城 entitlement 下的侧栏一级大类（非独立后端 moduleId） */
export const LIVE_SECTION = {
  moduleId: 'LIVE',
  sectionTitle: '直播管理',
  sectionIcon: 'ep:video-camera',
  /** 紧挨「商城管理」之后 */
  insertAfterModuleId: 'MALL' as const,
  menu: LIVE_MENU
} as const

export type ModuleId = 'MALL' | 'HOTEL' | 'O2O' | 'CAPACITY' | 'SCENIC' | 'RENTAL' | 'XUANXUE'

export interface ModuleDescriptor {
  moduleId: ModuleId
  sectionTitle: string
  /** 一级模块分组图标（侧栏 el-sub-menu 标题） */
  sectionIcon: string
  sortOrder: number
  enabled: boolean
  /** 兼容旧 consoleId */
  consoleId: string
  routePrefix: string
  homePath: string
  menu: ConsoleMenuItem[]
  apiAllowlist: string[]
}

/** 通用功能区（所有已开通商家可见；带 requireModule 的项按模块 entitlement 再筛） */
export const COMMON_MENU: ConsoleMenuItem[] = [
  // 暂不展示：模块切换入口；路由 /store/workbench 仍保留，登录回退可进
  // { path: storePath('/workbench'), title: '工作台', icon: 'ep:data-board' },
  {
    path: storePath('/belonging'),
    title: '归属代理',
    icon: 'ep:coordinate'
  },
  {
    path: storePath('/hotel/onboarding'),
    title: '酒店入驻',
    icon: 'ep:office-building',
    requireModule: 'HOTEL'
  },
  {
    path: storePath('/member/sub-account'),
    title: '员工角色',
    icon: 'ep:user-filled',
    permission: 'shop:member:sub-account:query'
  }
]

const MODULES: ModuleDescriptor[] = [
  {
    moduleId: 'O2O',
    sectionTitle: '上门管理',
    sectionIcon: 'ep:service',
    sortOrder: 10,
    enabled: true,
    consoleId: 'O2O_ORG',
    routePrefix: O2O.routePrefix,
    homePath: O2O.homePath,
    menu: O2O.menu,
    apiAllowlist: O2O.apiAllowlist
  },
  {
    moduleId: 'CAPACITY',
    sectionTitle: '运力管理',
    sectionIcon: 'ep:van',
    sortOrder: 20,
    enabled: true,
    consoleId: 'CAPACITY',
    routePrefix: CAPACITY.routePrefix,
    homePath: CAPACITY.homePath,
    menu: CAPACITY.menu,
    apiAllowlist: CAPACITY.apiAllowlist
  },
  {
    moduleId: 'MALL',
    sectionTitle: '商城管理',
    sectionIcon: 'ep:goods',
    sortOrder: 30,
    enabled: true,
    consoleId: 'MALL_SHOP',
    routePrefix: STORE_BASE,
    homePath: MALL.homePath,
    menu: MALL.menu.filter(
      // 员工角色已上提到 COMMON_MENU，避免商城区块重复；财务管理保留
      (m) => m.path !== storePath('/member/sub-account')
    ),
    apiAllowlist: MALL.apiAllowlist
  },
  {
    moduleId: 'SCENIC',
    sectionTitle: '景区管理',
    sectionIcon: 'ep:place',
    sortOrder: 35,
    enabled: true,
    consoleId: 'SCENIC_SHOP',
    routePrefix: SCENIC.routePrefix,
    homePath: SCENIC.homePath,
    menu: SCENIC.menu,
    apiAllowlist: SCENIC.apiAllowlist
  },
  {
    moduleId: 'HOTEL',
    sectionTitle: '酒店管理',
    sectionIcon: 'ep:office-building',
    sortOrder: 40,
    enabled: true,
    consoleId: 'HOTEL_SHOP',
    routePrefix: HOTEL.routePrefix,
    homePath: HOTEL.homePath,
    menu: HOTEL.menu,
    apiAllowlist: HOTEL.apiAllowlist
  },
  {
    moduleId: 'RENTAL',
    sectionTitle: '租赁管理',
    sectionIcon: 'ep:box',
    sortOrder: 45,
    enabled: true,
    consoleId: 'RENTAL_SHOP',
    routePrefix: RENTAL.routePrefix,
    homePath: RENTAL.homePath,
    menu: RENTAL.menu,
    apiAllowlist: RENTAL.apiAllowlist
  },
  {
    moduleId: 'XUANXUE',
    sectionTitle: '玄学机构',
    sectionIcon: 'ep:school',
    sortOrder: 50,
    enabled: true,
    consoleId: 'XUANXUE_SHOP',
    routePrefix: XUANXUE.routePrefix,
    homePath: XUANXUE.homePath,
    menu: XUANXUE.menu,
    apiAllowlist: XUANXUE.apiAllowlist
  }
]

export function listEnabledModules(): ModuleDescriptor[] {
  return MODULES.filter((m) => m.enabled).sort((a, b) => a.sortOrder - b.sortOrder)
}

export function getModule(moduleId: string | null | undefined): ModuleDescriptor | undefined {
  if (!moduleId) return undefined
  return MODULES.find((m) => m.moduleId === moduleId)
}

export function moduleIdFromConsole(consoleId: string): ModuleId | string {
  if (consoleId === 'MALL_SHOP') return 'MALL'
  if (consoleId === 'O2O_ORG') return 'O2O'
  if (consoleId === 'HOTEL_SHOP') return 'HOTEL'
  if (consoleId === 'SCENIC_SHOP') return 'SCENIC'
  if (consoleId === 'CAPACITY') return 'CAPACITY'
  if (consoleId === 'RENTAL_SHOP') return 'RENTAL'
  if (consoleId === 'XUANXUE_SHOP') return 'XUANXUE'
  return consoleId as ModuleId
}

/** 菜单项是否对当前权限集可见 */
export function isMenuItemAllowed(
  item: ConsoleMenuItem,
  permissions: Set<string> | string[]
): boolean {
  const permSet = permissions instanceof Set ? permissions : new Set(permissions)
  // 空集：主账号约定「全量」
  if (permSet.size === 0) return true
  if (permSet.has('merchant:*') || permSet.has('*')) return true

  const codes: string[] = []
  if (item.permission) codes.push(item.permission)
  if (item.anyPermissions?.length) codes.push(...item.anyPermissions)
  // 未声明权限码的入口：模块内默认可见（兼容过渡）
  if (codes.length === 0) return true
  return codes.some((code) => permSet.has(code))
}

/** 按权限码过滤菜单（主账号 permissions 为空集表示全量） */
export function filterMenuByPermissions(
  menu: ConsoleMenuItem[],
  permissions: Set<string> | string[],
  moduleId: ModuleId
): ConsoleMenuItem[] {
  const permSet = permissions instanceof Set ? permissions : new Set(permissions)
  if (permSet.size === 0) {
    return menu
  }
  // HOTEL / SCENIC / RENTAL / XUANXUE：按菜单项 permission 精细过滤
  if (
    moduleId === 'HOTEL' ||
    moduleId === 'SCENIC' ||
    moduleId === 'RENTAL' ||
    moduleId === 'XUANXUE'
  ) {
    return menu.filter((item) => isMenuItemAllowed(item, permSet))
  }
  if (hasModulePermission(permSet, moduleId)) {
    return menu.filter((item) => isMenuItemAllowed(item, permSet))
  }
  return []
}

/** 模块侧栏识别前缀：兼容 merchant:mall:* 种子码与接口真实码 shop:/trade:/merchant:order: */
const MODULE_PERM_PREFIXES: Record<ModuleId, string[]> = {
  MALL: [
    'merchant:mall:',
    'merchant:groupbuy:',
    'shop:product:',
    'shop:live:',
    'shop:promotion:',
    'shop:info:',
    'shop:finance:',
    'shop:category:',
    'shop:member:',
    'merchant:order:',
    'merchant:invoice:',
    'trade:order:',
    'trade:after-sale:',
    'trade:delivery:',
    'product:spu:'
  ],
  SCENIC: ['scenic:merchant:', 'merchant:scenic:'],
  O2O: ['merchant:o2o:'],
  CAPACITY: ['merchant:capacity:'],
  HOTEL: ['merchant:hotel:'],
  RENTAL: ['merchant:rental:', 'rental:'],
  XUANXUE: ['xuanxue:']
}

function hasModulePermission(permSet: Set<string>, moduleId: ModuleId): boolean {
  if (permSet.has('merchant:*') || permSet.has('*')) {
    return true
  }
  const prefixes = MODULE_PERM_PREFIXES[moduleId] || [`merchant:${moduleId.toLowerCase()}:`]
  return [...permSet].some((p) => prefixes.some((prefix) => p.startsWith(prefix)))
}

export function isPathAllowedForModules(path: string, activeModuleIds: string[]): boolean {
  if (path.startsWith(storePath('/login')) || path.startsWith(storePath('/b/'))) return true
  if (path === storePath('/workbench')) return true
  if (path.startsWith(storePath('/belonging'))) return true
  // 酒店入驻仅酒店商家可进
  if (path === storePath('/hotel/onboarding') || path.startsWith(storePath('/hotel/onboarding/'))) {
    return activeModuleIds.includes('HOTEL')
  }
  if (path === storePath('/member/sub-account')) return true
  for (const mid of activeModuleIds) {
    const m = getModule(mid)
    if (!m) continue
    if (mid === 'MALL') {
      if (
        path.startsWith(storePath('/o2o')) ||
        path.startsWith(storePath('/capacity')) ||
        path.startsWith(storePath('/hotel')) ||
        path.startsWith(storePath('/scenic'))
      )
        continue
      if (path.startsWith(STORE_BASE)) return true
    } else if (path.startsWith(m.routePrefix)) {
      return true
    }
  }
  return false
}
